import gsap, { Power2, Power3, Expo } from 'gsap';
import Lenis from 'lenis';

import { GetBy } from '../_app/cuchillo/core/Element';
import { Basics } from '../_app/cuchillo/core/Basics';
import { Accessibility } from '../_app/cuchillo/core/Accessibility';
import Keyboard, { KEYS } from '../_app/cuchillo/core/Keyboard';
import EventDispatcher from '../_app/cuchillo/core/EventDispatcher';

// Pop-up full-screen inspirat en el Sidemenu (bg que escala + content amb fade). És una
// classe estàtica autònoma (no estén el _Sidemenu base) per no heretar-ne el container
// ni els events globals. S'obre amb Popup.open(text): omple el camp de text cada cop.
// Dispatcha ON_SHOW/ON_HIDE (com el Sidemenu) → main.js hi enganxa el bloqueig d'scroll.
export default class Popup {
  static ON_SHOW = "popup_onshow";
  static ON_HIDE = "popup_onhide";

  static container = GetBy.id('Popup');
  static content;
  static bg;
  static textEl;
  static closeBtn;
  static isOpen = false;
  static _lenis = null; // Lenis intern del contingut (scroll suau), conduït per Popup.loop

  static init() {
    if (!this.container) return;

    this.content = GetBy.class('content', this.container)[0];
    this.bg = GetBy.class('bg', this.container)[0];
    this.textEl = GetBy.class('__text', this.container)[0];
    this.closeBtn = GetBy.selector('[data-close-popup]', this.container)[0];

    if (this.closeBtn) {
      this.closeBtn.addEventListener(Basics.clickEvent, () => this.hide());
    }

    this.directHide();
  }

  // Obre el popup omplint el camp de text (HTML) cada cop.
  static open(text = '') {
    if (!this.container) return;
    if (this.textEl) this.textEl.innerHTML = text;
    this.show();
  }

  static show() {
    if (this.isOpen) return;
    this.isOpen = true;

    this.container.style.visibility = 'visible';
    this.container.setAttribute('aria-expanded', 'true');

    Keyboard.add(KEYS.ESC, 'Popup_ESC', () => this.hide());
    Accessibility.trap(this.container);
    EventDispatcher.dispatchEvent(Popup.ON_SHOW);

    // Lenis intern per al scroll suau del contingut. wrapper = .content (overflow),
    // content = .text. autoRaf:false → el conduïm des de Popup.loop (RAF global).
    this._destroyLenis();
    this._lenis = new Lenis({ wrapper: this.content, content: this.textEl, lerp: 0.1, autoRaf: false });
    this._lenis.scrollTo(0, { immediate: true });

    gsap.killTweensOf(this.container);
    gsap.killTweensOf(this.bg);
    gsap.killTweensOf(this.content);

    gsap.set(this.container, { alpha: 0 });
    gsap.to(this.container, { alpha: 1, duration: 0.3, ease: Power3.easeOut });
    gsap.fromTo(this.bg,
      { scaleY: 0 },
      { scaleY: 1, transformOrigin: 'top', duration: 0.8, delay: 0.1, ease: Expo.easeOut });
    gsap.fromTo(this.content,
      { alpha: 0 },
      { alpha: 1, duration: 0.6, delay: 0.3, ease: Power3.easeOut });
  }

  static hide() {
    if (!this.isOpen) return;
    this.isOpen = false;

    this.container.setAttribute('aria-expanded', 'false');
    Keyboard.remove(KEYS.ESC, 'Popup_ESC');
    Accessibility.removeTrap();
    EventDispatcher.dispatchEvent(Popup.ON_HIDE);
    this._destroyLenis();

    gsap.killTweensOf(this.container);
    gsap.killTweensOf(this.bg);
    gsap.killTweensOf(this.content);

    gsap.to(this.content, { alpha: 0, duration: 0.2, ease: Power2.easeIn });
    gsap.to(this.bg, { scaleY: 0, transformOrigin: 'bottom', duration: 0.6, delay: 0.05, ease: Power2.easeOut });
    gsap.to(this.container, {
      alpha: 0, duration: 0.3, delay: 0.2, ease: Power2.easeIn,
      onComplete: () => this.directHide(),
    });
  }

  static directHide() {
    if (!this.container) return;
    this._destroyLenis();
    gsap.set(this.container, { alpha: 0 });
    if (this.bg) gsap.set(this.bg, { scaleY: 0 });
    this.isOpen = false;
    this.container.style.visibility = 'hidden';
    this.container.setAttribute('aria-expanded', 'false');
  }

  static _destroyLenis() {
    if (this._lenis) {
      this._lenis.destroy();
      this._lenis = null;
    }
  }

  // Conduït des del RAF global (main.loop). time en segons → Lenis espera ms.
  static loop(time) {
    this._lenis?.raf(time * 1000);
  }
}
